import type { DeviceFragment } from '@cobliteam/api-lib/src/__generated__/graphql';
import type { DeviceStatusEvent } from '@cobliteam/api-lib/src/dashboard/device/definitions';

declare global {
  interface Window {
    __deviceLastUpdateTime: Record<string, number>;
  }
}

if (typeof window !== 'undefined') {
  window.__deviceLastUpdateTime = {};
}

// Helper function to create updated device with new location data
const _createUpdatedDevice = (foundDevice: DeviceFragment, lastLocation: any): DeviceFragment => {
  return {
    ...foundDevice,
    lastLocation: {
      latitude: lastLocation.coordinate.latitude,
      longitude: lastLocation.coordinate.longitude,
      timeInMillis: lastLocation.date,
      heading: lastLocation.heading,
      ignitionOn: lastLocation.ignitionOn,
      speed: lastLocation.speed,
      idleOffDurationInMillis: lastLocation.idleOffDurationInMillis || foundDevice.lastLocation?.idleOffDurationInMillis,
      idleOnDurationInMillis: lastLocation.idleOnDurationInMillis || foundDevice.lastLocation?.idleOnDurationInMillis,
      cobliIngestedAt: lastLocation.ingestionTimeInMillis || foundDevice.lastLocation?.cobliIngestedAt,
      isPlugged: lastLocation.isPlugged || foundDevice.lastLocation?.isPlugged,
      pluggedUpdateTimestamp: lastLocation.pluggedUpdateTimestamp || foundDevice.lastLocation?.pluggedUpdateTimestamp,
    },
  };
};

// Calculate interval of time between receiving new values for a specific device
export const logDebugDeviceLastUpdateTime = (id: string): void => {
  if (!window.__deviceLastUpdateTime) {
    window.__deviceLastUpdateTime = {};
  }
  const now = performance.now();
  const lastTime = window.__deviceLastUpdateTime[id];
  if (lastTime !== undefined) {
    const intervalSeconds = ((now - lastTime) / 1000).toFixed(1);
    // You can log, store, or use this interval as needed
    // biome-ignore lint/suspicious/noConsole: Necessário para debug do intervalo de atualização dos dispositivos em desenvolvimento.
    console.warn(`Interval for device ${id}: ${intervalSeconds}s`);
  }
  window.__deviceLastUpdateTime[id] = now;
};

// Helper function to check if device location should be updated
const _shouldUpdateDeviceLocation = (foundDevice: DeviceFragment, lastLocation: any): boolean => {
  return !foundDevice.lastLocation || (!!foundDevice.lastLocation.timeInMillis && lastLocation.date > foundDevice.lastLocation.timeInMillis);
};

// Helper function to update devices array with new location data
export const updateDevicesWithLocationData = (devices: DeviceFragment[], deviceStatusEvents: DeviceStatusEvent[], updateEventsAmount: () => void): DeviceFragment[] => {
  let updatedDevices = devices;

  for (const deviceStatusEvent of deviceStatusEvents) {
    const { deviceId, ...lastLocation } = deviceStatusEvent;
    const deviceIndex = updatedDevices.findIndex((device) => device.id === deviceId);

    if (deviceIndex >= 0) {
      const foundDevice = updatedDevices[deviceIndex];

      if (_shouldUpdateDeviceLocation(foundDevice, lastLocation)) {
        if (deviceStatusEvents[0].deviceId === deviceId) {
          updateEventsAmount();
        }

        const updatedDevice = _createUpdatedDevice(foundDevice, lastLocation);
        updatedDevices = [...updatedDevices.slice(0, deviceIndex), updatedDevice, ...updatedDevices.slice(deviceIndex + 1)];
      }
    }
  }

  return updatedDevices;
};
